import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useFounderPlaces } from "@/components/FoundersClub";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight } from "lucide-react";

const PERKS = ["50% off every subscription plan for as long as you stay fully subscribed — renewals stay at the same rate", "50% off your first policy pack", "Founding Member badge and a numbered place (#1–#20 in your speciality), counted from your first payment", "Pay from day one (no free trial) · 12-month minimum term · cancelling inside it forfeits the discount and place", "One place per registered location · no VAT added · cancel in two clicks from Billing"];

export default function FoundersHero() {
  const { user } = useAuth();
  const data = useFounderPlaces();
  if (!data || user?.founder) return null;
  const open = data.places.filter((p) => p.left > 0);
  if (!open.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="landing-founders">
      <div className="relative overflow-hidden rounded-3xl border border-gold/50 bg-founders-card text-white">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-brandteal/20 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full bg-brandviolet/30 blur-3xl" aria-hidden="true" />
        <div className="relative grid lg:grid-cols-12 gap-8 items-center p-8 sm:p-12">
          <div className="lg:col-span-4 flex justify-center">
            <img src="/founders-club-logo.webp" alt="Futuregen Founders Club" className="w-56 h-56 sm:w-64 sm:h-64 rounded-full shadow-2xl ring-4 ring-gold/70 ring-offset-8 ring-offset-[#131027] object-cover" data-testid="landing-founders-logo" />
          </div>
          <div className="lg:col-span-8">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-gold mb-3">Only {data.campaign_total} places — {data.total} per speciality</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight mb-4">Join the Futuregen Founders Club — <span className="text-gold">{data.percent_off}% off for as long as you stay fully subscribed</span></h2>
            <p className="text-white/80 text-base md:text-lg mb-6 max-w-2xl">Just {data.total} founder places in each of our five specialities. Be one of them and lock in half-price compliance support: Ongoing Support £40/month or £400/year · Enterprise Multi-site £99.50/month or £995/year.</p>
            <ul className="grid sm:grid-cols-2 gap-2.5 mb-8">
              {PERKS.map((p) => <li key={p} className="flex items-start gap-2 text-sm text-white/90"><span className="w-5 h-5 rounded-full bg-gold/20 text-gold flex items-center justify-center mt-0.5 shrink-0"><Check className="w-3 h-3" /></span>{p}</li>)}
            </ul>
            <div className="flex flex-wrap gap-2 mb-8" data-testid="landing-founders-places">
              {data.places.map((p) => (
                <span key={p.speciality} className="rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs" data-testid={`landing-founders-place-${p.speciality.toLowerCase()}`}>
                  <span className="text-white/70">{p.label}</span> <span className="font-bold text-gold ml-1">{p.left > 0 ? `${p.taken} taken · ${p.left} left` : "Full"}</span>
                </span>
              ))}
            </div>
            {!user && (
              <Button asChild size="lg" className="rounded-full bg-gold text-gold-foreground hover:bg-gold/90 font-bold px-7 shadow-lg shadow-gold/20" data-testid="landing-founders-cta">
                <Link to="/register">Claim my founder place <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
